import { useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import {
  Button,
  Card,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSession } from '@client/model/selectors';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import {
  AdvisorCentricView,
  ClientCentricView,
  FlagCentricView,
  PendingChangesCart,
  PublishDialog,
  useAppConfigFeatureFlagsQuery,
  useFeatureFlagDraft,
} from '@/features/wizard-app-config-feature-flags';

const DEFAULT_TAB = 'by-flag';

function AppConfigIdLabel({
  label,
  id,
  name,
}: {
  label: string;
  id: string;
  name: string | null;
}) {
  return (
    <span title={id}>
      {label}{' '}
      {name ? (
        <>
          <span className="text-gray-900">{name}</span>
          <span className="text-muted-foreground ml-1 font-mono">({id})</span>
        </>
      ) : (
        <code className="font-mono">{id}</code>
      )}
    </span>
  );
}

export function WizardAppConfigFeatureFlags() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab') ?? DEFAULT_TAB;
  const [showCart, setShowCart] = useState(true);
  const [publishOpen, setPublishOpen] = useState(false);
  const { advisor } = useSelector(selectSession);
  const adminPortalUrl = advisor?.advisorId
    ? `/advisors/${advisor.advisorId}/admin-portal`
    : null;

  const onTabChange = (tab: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  const { data, isPending, isError, error, refetch } = useAppConfigFeatureFlagsQuery();
  const originalConfig = data?.config;
  const basedOnHash = data?.basedOnHash;
  const appConfigIds = data?.appConfigIds;
  const appConfigLabels = data?.appConfigLabels;

  const {
    operations,
    targetConfig,
    diff,
    isSaving,
    isBehindLatest,
    enqueueOp,
    removeOpAtIndex,
    clearAll,
  } = useFeatureFlagDraft({ original: originalConfig, basedOnHash });

  const publishDisabled = !diff.hasChanges || isSaving;

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      {isSaving && (
        <div
          aria-live="polite"
          className="fixed inset-0 z-50 flex items-start justify-center pt-6"
        >
          <div className="pointer-events-none flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 shadow-md">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            Saving draft…
          </div>
        </div>
      )}
      <div className="min-w-0">
        {adminPortalUrl && (
          <Link
            to={adminPortalUrl}
            className="text-muted-foreground mb-2 inline-flex items-center gap-1 text-sm hover:text-gray-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Advisor Admin Portal
          </Link>
        )}
        <DashboardPageHeader
          title="AppConfig Feature Flags"
          description="View and manage the AWS AppConfig feature flag configuration that drives runtime feature gating."
        />
        {appConfigIds && (
          <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
            <AppConfigIdLabel
              label="app"
              id={appConfigIds.applicationId}
              name={appConfigLabels?.applicationName ?? null}
            />
            <AppConfigIdLabel
              label="env"
              id={appConfigIds.environmentId}
              name={appConfigLabels?.environmentName ?? null}
            />
            <AppConfigIdLabel
              label="profile"
              id={appConfigIds.configurationProfileId}
              name={appConfigLabels?.configurationProfileName ?? null}
            />
          </div>
        )}
      </div>

      <PublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        diff={diff}
        basedOnHash={basedOnHash}
        onPublished={() => refetch()}
      />

      {isPending && (
        <div className="mt-6 space-y-2">
          <Skeleton className="h-10 w-full max-w-md" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {isError && (
        <Card className="mt-6 border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            Failed to load AppConfig:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </Card>
      )}

      {originalConfig && targetConfig && (
        <div className="mt-6">
          <Tabs
            value={selectedTab}
            onValueChange={onTabChange}
          >
            <TabsList className="mb-4">
              <TabsTrigger value="by-flag">By flag</TabsTrigger>
              <TabsTrigger value="by-advisor">By advisor</TabsTrigger>
              <TabsTrigger value="by-client">By client</TabsTrigger>
            </TabsList>

            <div className="flex items-start gap-6">
              <div className="min-w-0 flex-1">
                <TabsContent
                  value="by-flag"
                  className="mt-0"
                >
                  <FlagCentricView
                    originalConfig={originalConfig}
                    targetConfig={targetConfig}
                    onEnqueueOp={enqueueOp}
                    isSaving={isSaving}
                  />
                </TabsContent>

                <TabsContent
                  value="by-advisor"
                  className="mt-0"
                >
                  <AdvisorCentricView
                    originalConfig={originalConfig}
                    targetConfig={targetConfig}
                    onEnqueueOp={enqueueOp}
                    isSaving={isSaving}
                  />
                </TabsContent>

                <TabsContent
                  value="by-client"
                  className="mt-0"
                >
                  <ClientCentricView
                    originalConfig={originalConfig}
                    targetConfig={targetConfig}
                    onEnqueueOp={enqueueOp}
                    isSaving={isSaving}
                  />
                </TabsContent>
              </div>

              {showCart && (
                <aside className="sticky top-4 w-[28rem] shrink-0 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold">Draft</h2>
                    <div className="flex items-center gap-3">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                size="sm"
                                disabled={publishDisabled}
                                className="bg-primary hover:bg-primary-hover"
                                onClick={() => setPublishOpen(true)}
                              >
                                Publish changes
                                {diff.hasChanges && (
                                  <span className="ml-2 rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-mono">
                                    {diff.entries.length}
                                  </span>
                                )}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {diff.hasChanges
                              ? 'Review and publish draft to AWS AppConfig'
                              : 'Toggle a flag to start a draft.'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <button
                        className="text-muted-foreground text-xs hover:text-gray-900"
                        onClick={() => setShowCart(false)}
                      >
                        Hide
                      </button>
                    </div>
                  </div>
                  <PendingChangesCart
                    diff={diff}
                    operations={operations}
                    isSaving={isSaving}
                    isBehindLatest={isBehindLatest}
                    onRemoveOp={removeOpAtIndex}
                    onClearAll={clearAll}
                  />
                </aside>
              )}
              {!showCart && (
                <button
                  className="text-muted-foreground fixed right-4 top-20 rounded border bg-white px-2 py-1 text-xs shadow-sm hover:text-gray-900"
                  onClick={() => setShowCart(true)}
                >
                  Show draft ({diff.entries.length})
                </button>
              )}
            </div>
          </Tabs>
        </div>
      )}
    </main>
  );
}
